import { DecimalPipe, DOCUMENT } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { LoggerService } from '../../core/services/logger.service';
import { NfcAvailability, NfcFailureReason, NfcService, NfcTagContent } from '../../core/services/nfc.service';
import { QrScannerService } from '../../core/services/qr-scanner.service';
import { ScanCodeResolverService } from '../../core/services/scan-code-resolver.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

const NFC_READ_ERROR_KEYS: Partial<Record<NfcFailureReason, string>> = {
  unsupported: 'scan.nfc.error.unsupported',
  no_nfc: 'scan.nfc.error.noNfc',
  disabled: 'scan.nfc.error.disabled',
  permission_denied: 'scan.nfc.error.permissionDenied',
  busy: 'scan.nfc.error.busy',
  timeout: 'scan.nfc.error.timeout',
  tag_lost: 'scan.nfc.error.tagLost',
  unreadable: 'scan.nfc.error.unreadable',
  session_error: 'scan.nfc.error.sessionError',
};

/**
 * Component for scanning QR codes to look up assets
 * Uses @zxing/browser for camera-based QR code scanning
 */
@Component({
  selector: 'app-scan',
  imports: [
    CardComponent,
    CardContentComponent,
    IconComponent,
    ButtonComponent,
    SpinnerComponent,
    TranslatePipe,
    DecimalPipe,
  ],
  templateUrl: './scan.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './scan.component.css',
})
export class ScanComponent implements OnInit, OnDestroy {
  private scanCodeResolver = inject(ScanCodeResolverService);
  private router = inject(Router);
  private qrScanner = inject(QrScannerService);
  private nfc = inject(NfcService);
  private translation = inject(TranslationService);
  private document = inject(DOCUMENT);

  @ViewChild('videoElement', { static: false }) videoElement?: ElementRef<HTMLVideoElement>;

  private scanning = false;
  private readonly CAMERA_STORAGE_KEY = 'preferred-camera-device-id';

  cameraPermission = signal<'prompt' | 'granted' | 'denied'>('prompt');
  isScanning = signal(false);
  scanResult = signal<CheckPoint | null>(null);
  error = signal<string | null>(null);
  loading = signal(false);
  availableCameras = signal<MediaDeviceInfo[]>([]);
  selectedCameraId = signal<string | null>(null);
  showCameraSelector = signal(false);
  private logger = inject(LoggerService).getLogger('scan');
  private userStorage = inject(UserStorageService);
  private cameraTimeoutId: number | null = null;
  private progressIntervalId: number | null = null;
  private cameraStartTime: number | null = null;
  private cameraTimeoutMs = 10000;
  private readonly CAMERA_TIMEOUT_STORAGE_KEY = 'camera-auto-stop-timeout';
  private readonly DEFAULT_CAMERA_TIMEOUT_MS = 10000; // 10 seconds default

  // Progress bar signals
  progressPercent = signal(0);
  remainingTimeMs = signal(0);

  // NFC (native shell only; hidden when the device has no NFC). Android listens for tags
  // the whole time this page is open; iOS needs its system sheet, opened by a button.
  private nfcAvailability = signal<NfcAvailability | null>(null);
  nfcReady = computed(() => this.nfcAvailability()?.available === true);
  nfcPlatform = computed(() => {
    const availability = this.nfcAvailability();
    return availability?.available ? availability.platform : null;
  });
  nfcDisabled = computed(() => {
    const availability = this.nfcAvailability();
    return availability?.available === false && availability.reason === 'disabled';
  });
  /** iOS: the system NFC sheet is open */
  nfcScanning = signal(false);
  /** Android: listening for tags in the background */
  nfcListening = signal(false);
  private destroyed = false;
  private readonly onVisibilityChange = () => {
    // Re-check after the user returns from NFC settings
    if (this.document.visibilityState === 'visible' && !this.nfcScanning()) this.refreshNfcAvailability();
  };

  async ngOnInit() {
    this.refreshNfcAvailability();
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);

    // A tag link whose code isn't linked to anything lands here (tagLinkOpenGuard)
    const tagNotFound = new URLSearchParams(window.location.search).get('tagNotFound');
    if (tagNotFound) {
      this.error.set(this.translation.translate('scan.nfc.error.notFound', { code: tagNotFound }));
      return; // Don't auto-start the camera over the explanation
    }

    // Check if we have camera permission
    if ('permissions' in window.navigator) {
      try {
        const result = await window.navigator.permissions.query({
          name: 'camera' as PermissionName,
        });
        this.logger.debug('Camera permission state:', result.state);
        this.cameraPermission.set(result.state as 'prompt' | 'granted' | 'denied');
        result.onchange = () => {
          this.logger.debug('Camera permission changed to:', result.state);
          this.cameraPermission.set(result.state as 'prompt' | 'granted' | 'denied');
        };
      } catch (err) {
        this.logger.warn('Permissions API not fully supported', err);
      }
    }

    // Check for autoStart query parameter (from form submission)
    const autoStart = new URLSearchParams(window.location.search).get('autoStart');
    const shouldAutoStart = autoStart === 'true';

    // Auto-start scanning if we have a stored camera preference or only one camera
    const storedCameraId = this.userStorage.getItemWithGlobalFallback(this.CAMERA_STORAGE_KEY);
    if (storedCameraId || this.cameraPermission() === 'granted' || shouldAutoStart) {
      this.logger.debug('Auto-starting scan', shouldAutoStart ? '(from query param)' : '');
      // Small delay to ensure view is initialized
      setTimeout(() => this.startScanning(), 100);
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.nfcScanning() || this.nfcListening()) this.nfc.cancel();
    if (this.cameraTimeoutId !== null) {
      clearTimeout(this.cameraTimeoutId);
      this.cameraTimeoutId = null;
    }
    if (this.progressIntervalId !== null) {
      clearInterval(this.progressIntervalId);
      this.progressIntervalId = null;
    }
    this.stopScanning();
  }

  /**
   * Start QR code scanning
   */
  async startScanning() {
    this.logger.debug('startScanning called');
    this.logger.debug('videoElement available:', !!this.videoElement?.nativeElement);

    // Clear any existing timeout (user manually starting camera)
    if (this.cameraTimeoutId !== null) {
      clearTimeout(this.cameraTimeoutId);
      this.cameraTimeoutId = null;
    }

    this.error.set(null);
    this.scanResult.set(null);
    this.isScanning.set(true);

    try {
      // Get video devices
      this.logger.debug('Getting video devices...');
      const videoInputDevices = await this.qrScanner.getVideoDevices();

      if (videoInputDevices.length === 0) {
        this.logger.error('No cameras found');
        this.error.set('No camera found. Please ensure your device has a camera.');
        this.isScanning.set(false);
        return;
      }

      this.logger.debug(
        'Available cameras:',
        videoInputDevices.map((d, i) => `${i}: ${d.label} (${d.deviceId})`),
      );
      this.availableCameras.set(videoInputDevices);

      // Check if user has previously selected a camera
      const storedCameraId = this.userStorage.getItemWithGlobalFallback(this.CAMERA_STORAGE_KEY);
      let selectedDeviceId: string;

      if (videoInputDevices.length === 1) {
        // Only one camera - use it automatically
        selectedDeviceId = videoInputDevices[0].deviceId;
        this.logger.debug('Only one camera available, using:', videoInputDevices[0].label);
      } else if (storedCameraId && videoInputDevices.some((d) => d.deviceId === storedCameraId)) {
        // Use stored preference if it still exists
        selectedDeviceId = storedCameraId;
        const selectedDevice = videoInputDevices.find((d) => d.deviceId === storedCameraId);
        this.logger.debug('Using stored camera preference:', selectedDevice?.label);
      } else {
        // Multiple cameras and no valid stored preference - ask user to choose
        this.logger.debug('Multiple cameras available, asking user to choose');
        this.showCameraSelector.set(true);
        this.isScanning.set(false);
        return;
      }

      this.selectedCameraId.set(selectedDeviceId);

      // Start decoding from video device
      this.scanning = true;
      this.logger.debug('Starting decoder...');
      await this.qrScanner.startDecoding(
        selectedDeviceId,
        this.videoElement?.nativeElement,
        (result) => {
          if (result) {
            this.logger.debug('Scan success callback triggered');
            this.onScanSuccess(result);
          }
        },
      );

      this.logger.debug('Scanner started successfully');
      this.cameraPermission.set('granted');

      // Always set timeout to auto-stop camera
      // Get user's configured timeout (in seconds), default to 10 seconds
      const timeoutSeconds = parseInt(
        this.userStorage.getItem(this.CAMERA_TIMEOUT_STORAGE_KEY) || '10',
        10,
      );
      const timeoutMs = Math.max(5, Math.min(60, timeoutSeconds)) * 1000; // Clamp between 5-60 seconds (1 minute)
      this.cameraTimeoutMs = timeoutMs;

      this.logger.debug('Setting camera timeout for', timeoutMs, 'ms');

      // Start progress tracking
      this.cameraStartTime = Date.now();
      this.progressPercent.set(0);
      this.remainingTimeMs.set(timeoutMs);

      // Update progress every 100ms
      if (this.progressIntervalId !== null) {
        clearInterval(this.progressIntervalId);
      }
      this.progressIntervalId = window.setInterval(() => {
        if (this.cameraStartTime === null) return;

        const elapsed = Date.now() - this.cameraStartTime;
        const remaining = Math.max(0, this.cameraTimeoutMs - elapsed);
        const percent = Math.min(100, (elapsed / this.cameraTimeoutMs) * 100);

        this.progressPercent.set(percent);
        this.remainingTimeMs.set(remaining);
      }, 100);

      this.cameraTimeoutId = window.setTimeout(() => {
        this.logger.debug('Camera timeout reached, stopping scanner');
        this.stopScanning();
      }, timeoutMs);
    } catch (err: any) {
      this.logger.error('Failed to start scanning:', err);

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        this.cameraPermission.set('denied');
        this.error.set('Camera permission denied. Please allow camera access to scan QR codes.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        this.error.set('No camera found. Please ensure your device has a camera.');
      } else {
        this.error.set('Failed to start camera. Please try again.');
      }

      this.isScanning.set(false);
    }
  }

  /**
   * Stop QR code scanning
   */
  stopScanning() {
    // Clear timeout if camera was auto-started
    if (this.cameraTimeoutId !== null) {
      clearTimeout(this.cameraTimeoutId);
      this.cameraTimeoutId = null;
    }
    // Clear progress tracking interval
    if (this.progressIntervalId !== null) {
      clearInterval(this.progressIntervalId);
      this.progressIntervalId = null;
    }
    // Reset progress tracking
    this.cameraStartTime = null;
    this.progressPercent.set(0);
    this.remainingTimeMs.set(0);

    this.qrScanner.stop();
    this.scanning = false;
    this.isScanning.set(false);
  }

  /**
   * Handle successful QR code scan
   */
  async onScanSuccess(qrCode: string) {
    if (!this.scanning) return;

    this.logger.debug('QR Code scanned:', qrCode);
    this.stopScanning();
    this.loading.set(true);
    this.error.set(null);

    try {
      const resolution = await this.scanCodeResolver.resolveAndNavigate(qrCode);
      if (!resolution.found) {
        this.error.set(`No asset found with ID: ${qrCode}`);
      }
    } catch (err) {
      this.logger.error('Failed to process QR code:', err);
      this.error.set('Failed to process QR code. Please try again.');
    } finally {
      this.loading.set(false);
    }
  }

  async refreshNfcAvailability() {
    const availability = await this.nfc.getAvailability();
    if (this.destroyed) return;
    this.nfcAvailability.set(availability);
    if (availability.available && availability.platform === 'android' && !this.nfcListening()) {
      void this.listenForTags();
    }
  }

  /**
   * Android: keeps a reader session open while the page is open, so tapping a tag works
   * at any time (camera running or not) — no button. Ends when the page closes.
   */
  private async listenForTags() {
    this.nfcListening.set(true);
    try {
      while (!this.destroyed) {
        const result = await this.nfc.readTag({ timeoutMs: null });
        if (this.destroyed) return;
        if (!result.success) {
          this.logger.debug('NFC listening:', result.reason);
          // Cancelled: page closing · busy: another NFC action owns the reader
          if (result.reason === 'cancelled' || result.reason === 'busy') return;
          if (result.reason === 'disabled' || result.reason === 'no_nfc') {
            this.nfcAvailability.set({ available: false, reason: result.reason });
            return;
          }
          this.showNfcError(result.reason);
          continue;
        }
        await this.openNfcTag(result.tag);
      }
    } finally {
      this.nfcListening.set(false);
    }
  }

  /**
   * iOS: opens the system NFC sheet for one tag, then opens what its code points to
   * (same resolution as QR/barcodes)
   */
  async readNfcTag() {
    this.stopScanning();
    this.error.set(null);
    this.nfcScanning.set(true);
    const result = await this.nfc.readTag({ alertMessage: this.translation.translate('scan.nfc.iosAlert') });
    this.nfcScanning.set(false);

    if (!result.success) {
      this.logger.debug('NFC read failed:', result.reason);
      if (result.reason === 'cancelled') return;
      if (result.reason === 'disabled' || result.reason === 'no_nfc') this.refreshNfcAvailability();
      this.showNfcError(result.reason);
      return;
    }
    await this.openNfcTag(result.tag);
  }

  private showNfcError(reason: NfcFailureReason) {
    this.error.set(this.translation.translate(NFC_READ_ERROR_KEYS[reason] ?? 'scan.nfc.error.unreadable'));
  }

  private async openNfcTag(tag: NfcTagContent) {
    this.stopScanning();
    this.error.set(null);
    const text = tag.code ?? tag.texts[0];
    if (!text) {
      this.error.set(this.translation.translate(tag.blank ? 'scan.nfc.error.blank' : 'scan.nfc.error.foreign'));
      return;
    }

    this.loading.set(true);
    try {
      const resolution = await this.scanCodeResolver.resolveAndNavigate(text);
      if (!resolution.found) {
        this.error.set(
          tag.code
            ? this.translation.translate('scan.nfc.error.notFound', { code: tag.code })
            : this.translation.translate('scan.nfc.error.foreign'),
        );
      }
    } catch (err) {
      this.logger.error('Failed to process NFC tag:', err);
      this.error.set(this.translation.translate('scan.nfc.error.unreadable'));
    } finally {
      this.loading.set(false);
    }
  }

  cancelNfc() {
    this.nfc.cancel();
  }

  openNfcSettings() {
    this.nfc.openSettings();
  }

  /**
   * Navigate to record interaction flow
   */
  recordInteraction() {
    const checkPoint = this.scanResult();
    if (checkPoint) {
      this.router.navigate(['/submission', checkPoint.id]);
    }
  }

  /**
   * Navigate to asset detail view
   */
  viewCheckPoint() {
    const checkPoint = this.scanResult();
    if (checkPoint) {
      this.router.navigate(['/checkpoints', checkPoint.id]);
    }
  }

  /**
   * User selects a camera from the available options
   */
  async selectCamera(deviceId: string) {
    this.logger.debug('User selected camera:', deviceId);
    this.userStorage.setItemBoth(this.CAMERA_STORAGE_KEY, deviceId);
    this.selectedCameraId.set(deviceId);
    this.showCameraSelector.set(false);
    await this.startScanning();
  }

  /**
   * Allow user to change camera (show selector again)
   */
  changeCamera() {
    this.logger.debug('User wants to change camera');
    this.stopScanning();
    this.showCameraSelector.set(true);
  }

  /**
   * Scan another QR code (used after an error)
   */
  scanAnother() {
    this.error.set(null);
    this.scanResult.set(null);
    this.startScanning();
  }
}
